import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight, Phone, Sparkles } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/h4b1vodl_WhatsApp%20Image%202026-05-05%20at%2016.55.35.jpeg";
const HERO_IMG_2 =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/3k50gg6v_WhatsApp%20Image%202026-05-05%20at%2016.55.34.jpeg";

/** Line-mask reveal: each row wraps text in an overflow-hidden band; y-translate from below */
const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      className="inline-block"
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const sectionRef = useRef(null);
  const reduced = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -80]);
  const ySecond = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -140]);
  const ySticker = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : -40]);
  const yYellowBox = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 60]);
  const yCyanBox = useTransform(scrollYProgress, [0, 1], [0, reduced ? 0 : 120]);

  return (
    <section
      id="top"
      ref={sectionRef}
      className="relative overflow-hidden border-b-4 border-black"
    >
      {/* Background parallax shapes */}
      <div className="absolute top-0 right-0 w-1/2 h-full pointer-events-none hidden lg:block">
        <motion.div
          style={{ y: yYellowBox }}
          className="absolute top-10 right-10 w-40 h-40 bg-[#FFE600] nb-border rotate-12"
        />
        <motion.div
          style={{ y: yCyanBox }}
          className="absolute bottom-20 right-48 w-24 h-24 bg-[#00F0FF] nb-border -rotate-6"
        />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 pt-14 md:pt-20 pb-20 lg:pb-28 grid grid-cols-12 gap-8 lg:gap-12">
        {/* Left copy */}
        <div className="col-span-12 lg:col-span-7 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 nb-border-2 bg-white px-3 py-1.5 mb-6"
            data-testid="hero-badge"
          >
            <Sparkles size={14} strokeWidth={3} className="text-[#FF0055]" />
            <span className="font-heading uppercase text-xs tracking-widest">
              Custom Printing · Apparel · Workwear
            </span>
          </motion.div>

          <h1
            className="font-heading uppercase text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.92] tracking-tighter"
            data-testid="hero-title"
          >
            <MaskedLine delay={0.05}>
              <>
                We print{" "}
                <span className="bg-[#FFE600] nb-border-2 px-2 inline-block">bold</span>
                ,
              </>
            </MaskedLine>
            <MaskedLine delay={0.22}>
              <>
                you wear the <span className="text-[#FF0055]">story.</span>
              </>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="mt-6 max-w-xl text-lg sm:text-xl text-[#0A0A0A]/80 leading-relaxed"
            data-testid="hero-subtitle"
          >
            Customade is a custom t-shirt and apparel manufacturer in Mumbai
            (Nagpada, Madanpura). Sublimation cricket jerseys, cotton
            round-neck tees, pique polos, oversized streetwear, fleece hoodies,
            boiler suits, windcheaters, corporate uniforms and promotional
            merchandise — bulk orders from 25 pieces, pan-India delivery.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href="#contact" className="nb-btn nb-btn-magenta" data-testid="hero-quote-cta">
              Get a Quote <ArrowRight size={18} strokeWidth={3} />
            </a>
            <a href="tel:8169272803" className="nb-btn" data-testid="hero-call-cta">
              <Phone size={16} strokeWidth={3} />
              8169272803
            </a>
          </motion.div>

          {/* Stat row */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.85 }}
            className="mt-12 grid grid-cols-3 max-w-lg"
            data-testid="hero-stats"
          >
            <div className="border-r-2 border-black pr-4">
              <div className="font-heading text-3xl md:text-4xl">500+</div>
              <div className="text-xs uppercase tracking-wider text-[#555]">Happy Clients</div>
            </div>
            <div className="border-r-2 border-black px-4">
              <div className="font-heading text-3xl md:text-4xl">24h</div>
              <div className="text-xs uppercase tracking-wider text-[#555]">Quick Quotes</div>
            </div>
            <div className="pl-4">
              <div className="font-heading text-3xl md:text-4xl">4K+</div>
              <div className="text-xs uppercase tracking-wider text-[#555]">Pieces Printed</div>
            </div>
          </motion.div>
        </div>

        {/* Right images with clipped mask entrance + scroll parallax */}
        <div className="col-span-12 lg:col-span-5 relative min-h-[360px] lg:min-h-[560px]">
          <motion.div
            style={{ y: yMain }}
            initial={{ opacity: 0, x: 20, rotate: 6, clipPath: "inset(100% 0 0 0)" }}
            animate={{
              opacity: 1,
              x: 0,
              rotate: 3,
              clipPath: "inset(0% 0 0 0)",
            }}
            transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute top-0 right-0 w-[78%] h-[62%] nb-border-4 nb-shadow-lg overflow-hidden bg-white"
            data-testid="hero-image-main"
          >
            <img
              src={HERO_IMG}
              alt="RK Star 52 cricket team in custom light-blue sublimation jerseys"
              className="w-full h-full object-cover"
              loading="eager"
            />
            {/* Spotlight vignette */}
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,transparent_40%,rgba(0,0,0,0.35)_100%)]" />
          </motion.div>
          <motion.div
            style={{ y: ySecond }}
            initial={{ opacity: 0, y: 30, rotate: -8, clipPath: "inset(0 0 100% 0)" }}
            animate={{
              opacity: 1,
              y: 0,
              rotate: -4,
              clipPath: "inset(0 0 0% 0)",
            }}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-0 left-0 w-[62%] h-[48%] nb-border-4 nb-shadow overflow-hidden bg-[#00F0FF]"
            data-testid="hero-image-secondary"
          >
            <img
              src={HERO_IMG_2}
              alt="UNIX cricket team celebrating with trophy in custom red-and-black jerseys"
              className="w-full h-full object-cover"
              loading="eager"
            />
          </motion.div>

          {/* Floating sticker */}
          <motion.div
            style={{ y: ySticker }}
            initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
            animate={{ opacity: 1, scale: 1, rotate: -12 }}
            transition={{ duration: 0.6, delay: 0.9, type: "spring", stiffness: 200 }}
            className="absolute top-6 left-4 bg-[#FFE600] nb-border-4 px-4 py-3 nb-shadow-sm"
            data-testid="hero-sticker"
          >
            <div className="font-heading uppercase text-xs leading-tight">
              Made in<br />Mumbai
            </div>
          </motion.div>
        </div>
      </div>

      {/* Signature scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 pointer-events-none hidden md:flex"
        aria-hidden
      >
        <span className="font-heading uppercase text-[10px] tracking-[0.4em] text-[#0A0A0A]/60">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="w-6 h-6 nb-border-2 bg-white flex items-center justify-center"
        >
          <ArrowRight size={12} strokeWidth={3} className="rotate-90" />
        </motion.span>
      </motion.div>
    </section>
  );
}
